/**
 * ResponsiveScaleProvider
 *
 * This component calculates and provides a responsive scale factor based on the relationship
 * between a container's dimensions and a viewBox's dimensions. It ensures content properly
 * scales to fit available space while maintaining aspect ratio.
 *
 * The scale factor is provided to child components via React Context.
 *
 * Performance optimizations:
 * - Uses useMemo instead of useState + useEffect for scale calculations
 * - Memoizes all functions with useCallback to prevent unnecessary recreations
 * - Memoizes complex objects with useMemo to maintain referential equality
 * - Uses React.memo on the exported component to prevent unnecessary rerenders
 */
import React, { ReactNode, useCallback, useMemo } from 'react';
import { ResponsiveScaleContext } from './ResponsiveScaleContext';
import { useResponsiveScaleSharedValues } from '../hooks/useResponsiveScaleSharedValues';
import { SharedValue } from 'react-native-reanimated';

/**
 * Props for the ResponsiveScaleProvider component
 * @property {ReactNode} children - Child components that will have access to the scale context
 * @property {{ height: number; width: number }} containerViewBox - Optional outer viewBox dimensions for nested scaling scenarios
 * @property {{ height: number; width: number }} viewBox - Required dimensions of the content that needs to be scaled
 * @property {SharedValue<{width: number, height: number}>} containerDimensionsSV - Optional direct container dimensions for optimized performance
 */
interface ResponsiveScaleProviderProps {
  children: ReactNode;
  containerViewBox?: { width: number; height: number };
  viewBox: { width: number; height: number };
  containerDimensionsSV?: SharedValue<{ width: number; height: number }>;
}



/**
 * A provider component that calculates and provides a responsive scale factor
 * to its children based on container size and content dimensions.
 *
 * The component handles two scaling scenarios:
 * 1. Direct scaling: viewBox is scaled directly based on window/container size
 * 2. Nested scaling: viewBox is scaled within another scaled container (containerViewBox)
 */
const ResponsiveScaleProvider = ({ children, viewBox, containerViewBox, containerDimensionsSV }: ResponsiveScaleProviderProps) => {
  // Get SharedValues and React values using the unified hook (single source of truth)
  const {
    responsiveScaleSV,
    containerResponsiveScaleSV,
    responsiveScale,
    containerResponsiveScale
  } = useResponsiveScaleSharedValues({
    viewBox,
    containerViewBox,
    containerDimensionsSV,
  });


  /**
   * Utility function to scale a single numeric value
   * Memoized with useCallback to maintain reference equality between renders
   */
  const scaleValue = useCallback(
    (value: number): number => {
      return value * responsiveScale;
    },
    [responsiveScale],
  );

  /**
   * Utility function to unscale a single numeric value (inverse of scale)
   * Useful for converting screen coordinates back to logical coordinates
   */
  const unscaleValue = useCallback(
    (value: number): number => {
      return value / responsiveScale;
    },
    [responsiveScale],
  );

  /**
   * Utility function to scale all numeric properties of an object
   * Returns a new object with scaled values, preserving non-numeric properties
   * Memoized as a function factory to avoid recreation when dependencies change
   */
  const scaleObject = useMemo(() => {
    return <T extends object>(obj: T): T => {
      const result = { ...obj } as T;
      for (const key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          const value = result[key];
          if (typeof value === 'number') {
            (result[key] as any) = scaleValue(value);
          }
        }
      }
      return result;
    };
  }, [scaleValue]);

  /**
   * Utility function to unscale all numeric properties of an object
   * Returns a new object with unscaled values, preserving non-numeric properties
   */
  const unscaleObject = useMemo(() => {
    return <T extends object>(obj: T): T => {
      const result = { ...obj } as T;
      for (const key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          const value = result[key];
          if (typeof value === 'number') {
            (result[key] as any) = unscaleValue(value);
          }
        }
      }
      return result;
    };
  }, [unscaleValue]);

  /**
   * Create the context value object containing all scale factors and utility functions
   * Memoized to prevent unnecessary rerenders of consuming components
   * This is a critical optimization that ensures context consumers only rerender
   * when the actual scale values or functions change
   */
  const contextValue = useMemo(() => {
    return {
      responsiveScale,
      containerResponsiveScale,
      responsiveScaleSV,
      containerResponsiveScaleSV,
      scaleValue,
      unscaleValue,
      scaleObject,
      unscaleObject,
    };
  }, [
    responsiveScale,
    containerResponsiveScale,
    responsiveScaleSV,
    containerResponsiveScaleSV,
    scaleValue,
    unscaleValue,
    scaleObject,
    unscaleObject
  ]);

  // Provide the context value to all children
  return <ResponsiveScaleContext.Provider value={contextValue}>{children}</ResponsiveScaleContext.Provider>;
};

/**
 * Export a memoized version of the component to prevent unnecessary rerenders
 * when parent components rerender but the props haven't changed
 */
export default React.memo(ResponsiveScaleProvider);